'use client';

import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import {
    GRUND,
    Scen,
    TYPSNITT,
    TextAvtäckning,
    type Kombonamn,
    type ScenKontext
} from 'src/components/rörelse';
import { VisionsMarkering } from './VisionsMarkering';

type Props = {
    id: string;
    kombo: Kombonamn;
    rubrik: string;
    /** En eller flera stycken. Sätts på 65–75 teckens mått. */
    brödtext?: string | string[];
    /** Sant när scenen beskriver funktion som ligger senare i ordningen. */
    vision?: boolean;
    visionstext?: string;
    /** Lägger textkolumnen till höger i stället. Bryter rytmen mellan scener. */
    spegla?: boolean;
    /** Rubriknivå. Sidans första scen bär h1, övriga h2. */
    nivå?: 'h1' | 'h2';
    höjd?: 'skärm' | 'auto';
    onSynlig?: (synlig: boolean) => void;
    children?: ReactNode | ((kontext: ScenKontext) => ReactNode);
};

export function ScenSektion({
    id,
    kombo,
    rubrik,
    brödtext,
    vision,
    visionstext,
    spegla,
    nivå = 'h2',
    höjd = 'skärm',
    onSynlig,
    children
}: Props) {
    const stycken = brödtext === undefined ? [] : Array.isArray(brödtext) ? brödtext : [brödtext];

    return (
        <Scen id={id} höjd={höjd} onSynlig={onSynlig}>
            {(kontext) => (
                <Box
                    sx={{
                        display: 'grid',
                        gap: { xs: 6, md: 8 },
                        alignItems: 'center',
                        gridTemplateColumns: { xs: '1fr', md: spegla ? '1.15fr 0.85fr' : '0.85fr 1.15fr' },
                        maxWidth: 1320,
                        mx: 'auto',
                        width: '100%'
                    }}
                >
                    <Box
                        sx={{
                            order: { xs: 1, md: spegla ? 2 : 1 },
                            maxWidth: '62ch'
                        }}
                    >
                        {vision ? (
                            <Box sx={{ mb: 2.5 }}>
                                <VisionsMarkering kombo={kombo} text={visionstext} />
                            </Box>
                        ) : null}

                        <TextAvtäckning
                            text={rubrik}
                            component={nivå}
                            variant={nivå === 'h1' ? 'h1' : 'h2'}
                            avstånd={22}
                            sx={{
                                fontFamily: TYPSNITT.display,
                                fontVariationSettings: `'opsz' ${nivå === 'h1' ? 108 : 72}`,
                                fontWeight: nivå === 'h1' ? 600 : 500,
                                fontSize: nivå === 'h1' ? 'clamp(2.6rem, 6.2vw, 5.4rem)' : 'clamp(1.9rem, 3.9vw, 3.3rem)',
                                lineHeight: 1.03,
                                letterSpacing: '-0.028em',
                                textWrap: 'balance',
                                color: GRUND.text,
                                mb: stycken.length ? 3 : 0
                            }}
                        />

                        {stycken.map((stycke, i) => (
                            <TextAvtäckning
                                key={i}
                                text={stycke}
                                variant="body1"
                                fördröjning={120 + i * 90}
                                avstånd={12}
                                sx={{
                                    fontSize: { xs: '1.02rem', md: '1.12rem' },
                                    lineHeight: 1.62,
                                    color: GRUND.dämpad,
                                    mt: i ? 2 : 0
                                }}
                            />
                        ))}
                    </Box>

                    <Box
                        sx={{
                            order: { xs: 2, md: spegla ? 1 : 2 },
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        {typeof children === 'function' ? children(kontext) : children}
                    </Box>
                </Box>
            )}
        </Scen>
    );
}
